import { Box, Snackbar, Alert } from "@mui/material";
import ListComponent from "../../containers/PageTemplate/ListComponent";
import { useEffect, useState } from "react";
import Form from "../../containers/PageTemplate/Form";
import GridComponent from "../../containers/PageTemplate/GridComponent";
import { useNavigate } from "react-router-dom";
import DeleteModal from "../../containers/PageTemplate/DeleteModal";
import {
  createDevice,
  getDevices,
  getDeviceDetails,
  updateDevice,
  deleteDevice,
} from "../api/devices";

const localDeviceDefinition = {
  id: null,
  deviceName: "",
  deviceStatus: "PENDING",
};

const Devices = () => {
  const [devicesData, setDevicesData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [formObject, setFormObject] = useState(localDeviceDefinition);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [layoutView, setLayoutView] = useState("list");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalItemCount, setDeleteModalItemCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handleClose = (event, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  const handleLayoutChange = (layout) => {
    setLayoutView(layout);
  };

  const columns = [
    {
      id: "deviceName",
      label: "Thrust Name",
      type: "textfield",
      display: true,
      render: (row) => row.deviceName,
      onClick: (row) => navigate(`${row.id}`),
    },
    {
      id: "deviceStatus",
      label: "Status",
      type: "dropdown",
      display: true,
      options: [
        { value: "ACTIVE", label: "Active" },
        { value: "INACTIVE", label: "Inactive" },
        { value: "PENDING", label: "Pending" },
      ],
      default: "PENDING",
      render: (row) => row.deviceStatus,
    },
  ];

  const getDevicesList = async () => {
    try {
      setIsLoading(true);
      const devices = await getDevices();
      setDevicesData(devices);
    } catch (error) {
      console.error("Error fetching devices:", error);
      setSnackbarMessage("Failed to load devices");
      setSnackbarSeverity("error");
      setOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddClick = async (newDeviceObject) => {
    try {
      setIsLoading(true);

      const apiDevice = {
        deviceName: newDeviceObject.deviceName,
        deviceStatus: newDeviceObject.deviceStatus,
      };

      const createdDeviceResponse = await createDevice(apiDevice);
      const createdDeviceId = createdDeviceResponse?.createdDevice?.deviceId;
      await getDevicesList();
      
      setShowAddForm(false);
      setFormObject(localDeviceDefinition);
      setSnackbarMessage("Device created successfully");
      setSnackbarSeverity("success");
      setOpen(true);

      if (createdDeviceId) {
        navigate(`${createdDeviceId}/manual-control`);
      }
    } catch (error) {
      console.error("Error creating device:", error);
      setSnackbarMessage("Failed to create device");
      setSnackbarSeverity("error");
      setOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = async (editedDeviceObject) => {
    try {
      setIsLoading(true);
      await updateDevice(editedDeviceObject);
      await getDevicesList();
      
      setShowEditForm(false);
      setFormObject(localDeviceDefinition);
      setSnackbarMessage("Device updated successfully");
      setSnackbarSeverity("success");
      setOpen(true);
    } catch (error) {
      console.error("Error updating device:", error);
      setSnackbarMessage("Failed to update device");
      setSnackbarSeverity("error");
      setOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (selectedRows.length === 0) return;

    try {
      setIsLoading(true);
      // Delete all selected devices
      await Promise.all(selectedRows.map(row => deleteDevice(row.id)));
      
      await getDevicesList();
      setSelectedRows([]);
      setShowDeleteModal(false);
      setSnackbarMessage(`${selectedRows.length} device${selectedRows.length > 1 ? 's' : ''} deleted successfully`);
      setSnackbarSeverity("success");
      setOpen(true);
    } catch (error) {
      console.error("Error deleting devices:", error);
      setSnackbarMessage("Failed to delete device(s)");
      setSnackbarSeverity("error");
      setOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDeleteModal = (count) => {
    setDeleteModalItemCount(count);
    setShowDeleteModal(true);
  };

  const handleRefreshClick = () => {
    getDevicesList();
  };

  const handleActionSelect = async (action, selectedDeviceIds) => {
    setSelectedRows(selectedDeviceIds);
    
    if (action === "Modify" && selectedDeviceIds.length === 1) {
      try {
        setIsLoading(true);
        const deviceDetails = await getDeviceDetails(selectedDeviceIds[0]);
        setFormObject(deviceDetails);
        setShowEditForm(true);
      } catch (error) {
        console.error("Error fetching device details:", error);
        setSnackbarMessage("Failed to load device details");
        setSnackbarSeverity("error");
        setOpen(true);
      } finally {
        setIsLoading(false);
      }
    } else if (action === "Delete") {
      if (selectedDeviceIds?.length > 0) {
        handleOpenDeleteModal(selectedDeviceIds.length);
      }
    }
  };

  useEffect(() => {
    getDevicesList();
  }, []);

  return (
    <Box
      sx={{
        padding: 2,
        boxSizing: "border-box",
        height: "100%",
        position: "relative",
      }}
    >
      <Snackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleClose}
          severity={snackbarSeverity}
          sx={{ width: "100%", color: "white" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      
      {layoutView === "list" ? (
        <ListComponent
          title="Thrust Stands"
          columns={columns}
          showAddButton={true}
          addButtonText="Add Thrust Stand"
          showActions={true}
          actionOptions={[
            { value: "Modify", label: "Modify" },
            { value: "Delete", label: "Delete" },
          ]}
          data={devicesData || []}
          onRefresh={handleRefreshClick}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          setShowAddForm={setShowAddForm}
          setShowUpdateForm={setShowEditForm}
          onOptions={handleActionSelect}
          onDeleteClick={handleOpenDeleteModal}
          formObject={formObject}
          setFormObject={setFormObject}
          object="device"
          isLoading={isLoading}
        />
      ) : (
        <GridComponent
          title="Thrust Stands"
          columns={columns}
          showAddButton={true}
          addButtonText="Add Thrust Stand"
          showActions={true}
          actionOptions={[
            { value: "Modify", label: "Modify" },
            { value: "Delete", label: "Delete" },
          ]}
          data={devicesData || []}
          onRefresh={handleRefreshClick}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          setShowAddForm={setShowAddForm}
          onOptions={handleActionSelect}
          formObject={formObject}
          setFormObject={setFormObject}
          onLayoutChange={handleLayoutChange}
          layoutView={layoutView}
          object="device"
          isLoading={isLoading}
        />
      )}

      {/* Add Device Form */}
      {showAddForm && (
        <Form
          formTitle="Add Thrust Stand"
          object="device"
          columns={columns.filter((col) => col.id !== "id")}
          formObject={formObject}
          setFormObject={setFormObject}
          setShowForm={setShowAddForm}
          submitButtonText="Add"
          onSubmit={() => handleAddClick(formObject)}
          isLoading={isLoading}
        />
      )}

      {/* Edit Device Form */}
      {showEditForm && (
        <Form
          formTitle="Edit Thrust Stand"
          object="device"
          columns={columns.filter((col) => col.id !== "id")}
          formObject={formObject}
          setFormObject={setFormObject}
          setShowForm={setShowEditForm}
          submitButtonText="Save Changes"
          onSubmit={() => handleEditClick(formObject)}
          isLoading={isLoading}
        />
      )}

      <DeleteModal
        open={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleDelete={handleDeleteConfirmed}
        item={`${deleteModalItemCount} device${deleteModalItemCount > 1 ? "s" : ""}`}
        isLoading={isLoading}
      />
    </Box>
  );
};

export default Devices;
